/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any
import { useStackNavigate } from '@/app/navigation/stack-links';
import { completeConfig } from '@/components/ui/data-table/config';
import { DataTable } from '@/components/ui/data-table/data-table';
import { DataTableConsumer } from '@/components/ui/data-table/types';
import { LabelBadge } from '@/components/ui/LabelBadge';
import { labelsTableConfig } from '@/config/table-configs';
import { labelFields } from '@/features/labels/labels.fields';
import { useFilterableTable } from '@/hooks/useFilterableTable';
import { usePanelFilterState } from '@/hooks/usePanelFilterState';
import { usePanelSelectionState } from '@/hooks/usePanelSelectionState';
import { usePersistedTableConfig } from '@/hooks/usePersistedTableConfig';
import { ShareContent } from '@/utils/share';
import { Label } from '@certhon/models';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { omit } from 'remeda';
import { getLabelActions } from './labels.actions';

export { DataTable } from '@/components/ui/data-table/data-table';

const fields = omit(labelFields, ['color', 'name']);

const emptyArray: any[] = [];

export function LabelsTable({
  uriPrefix = '',
  data,
  isLoading,
}: {
  uriPrefix?: string;
} & DataTableConsumer<Label>) {
  const { filterValue, setFilterValue } = usePanelFilterState();
  const { rowSelection, setRowSelection, clearSelection } =
    usePanelSelectionState();

  // Clear selection when filter changes (context change)
  const prevFilterRef = useRef(filterValue);
  useEffect(() => {
    if (prevFilterRef.current !== filterValue) {
      clearSelection();
      prevFilterRef.current = filterValue;
    }
  }, [filterValue, clearSelection]);

  // Use schema-driven filter system
  const {
    tokenDefinitions,
    filteredData,
    columns,
    isFiltering,
    resultCount,
    totalCount,
  } = useFilterableTable<Label>({
    fields,
    data: data || emptyArray,
    filterValue,
    customColumns: [
      // Override name column to use LabelBadge component
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name' as keyof Label,
        cell: ({ row }) => (
          <LabelBadge label={row.original} deleted={row.original.deleted} />
        ),
      },
    ] as ColumnDef<Label>[],
  });

  const completeDefaultConfig = useMemo(
    () => completeConfig(labelsTableConfig, columns),
    [columns],
  );

  const [tableConfig, setTableConfig] = usePersistedTableConfig(
    'labels',
    completeDefaultConfig,
  );

  // Get row actions for labels
  const rowActions = useMemo(() => getLabelActions(), []);

  const navigate = useStackNavigate();

  const createUriPath = useCallback(
    (label: Label): string => [uriPrefix, '/labels/', label.recnum].join(''),
    [uriPrefix],
  );

  const createShareContent = useCallback(
    (label: Label, url: string): ShareContent => ({
      title: `Label: ${label.name}`,
      text: label.description || `Check out this label: ${label.name}`,
      url,
    }),
    [],
  );

  const addPath = `${uriPrefix}/labels/new`;

  const handleNew = useCallback(() => {
    navigate({ to: addPath });
  }, [navigate, addPath]);

  return (
    <DataTable<Label, any>
      isLoading={isLoading}
      data={filteredData}
      columns={columns}
      config={tableConfig}
      onConfigChange={setTableConfig}
      actions={rowActions}
      createUriPath={createUriPath}
      createShareContent={createShareContent}
      onNew={handleNew}
      filterValue={filterValue}
      onFilterChange={setFilterValue}
      tokenDefinitions={tokenDefinitions}
      isFiltering={isFiltering}
      resultCount={resultCount}
      totalCount={totalCount}
      entityName="labels"
      addPath={addPath}
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
    />
  );
}

export default LabelsTable;
